import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../lib/api";
import { useRoom } from "./RoomProvider";
import { ZoneBadge, SameZoneToggle } from "../../components/ZoneBadge";
import { useZones } from "../../hooks/useZones";

/**
 * CPDLane — highly-visible Chicago Police Department transport lane.
 *
 * Dedicated CPD line intakes land here in real time (backend: cpd_program.py).
 * A VA claims → picks a cleared/on-shift driver → the 1-hour SLA clock starts.
 * Backed by GET /api/cpd/intakes (5s poll), same visual urgency language as
 * the SOS system elsewhere on The Floor (red = breach risk).
 */
function useCountdown(deadline) {
  const [msLeft, setMsLeft] = useState(null);
  useEffect(() => {
    if (!deadline) { setMsLeft(null); return; }
    const tick = () => setMsLeft(new Date(deadline).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  return msLeft;
}

function fmtCountdown(ms) {
  if (ms == null) return "—";
  const neg = ms < 0;
  const abs = Math.abs(ms);
  const m = Math.floor(abs / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const txt = `${m}:${String(s).padStart(2, "0")}`;
  return neg ? `-${txt} OVER` : txt;
}

function IntakeCard({ intake, drivers, onClaim, onAssign, onResolve, callSession, riskAlert, onResolveRisk, onArchiveTrip, onArchiveIntake, onDeleteIntake }) {
  const [assigning, setAssigning] = useState(false);
  const [driverId, setDriverId] = useState("");
  const [forceUncleared, setForceUncleared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preTripBusy, setPreTripBusy] = useState(false);
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [sameZoneOnly, setSameZoneOnly] = useState(false);
  const { zoneForAddress } = useZones();
  const [trackOpen, setTrackOpen] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailInput, setEmailInput] = useState(intake.officer_email || "");
  const [emailSaving, setEmailSaving] = useState(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [phoneInput, setPhoneInput] = useState(intake.officer_phone || "");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [kaizenEditing, setKaizenEditing] = useState(false);
  const [kaizenInput, setKaizenInput] = useState(intake.kaizen_trip_id || "");
  const [kaizenSaving, setKaizenSaving] = useState(false);
  const [tripIdCopied, setTripIdCopied] = useState(false);
  const [genTripIdBusy, setGenTripIdBusy] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveReason, setResolveReason] = useState("");
  const [resolving, setResolving] = useState(false);
  const [riskBusy, setRiskBusy] = useState(false);

  const deadline = intake.status === "assigned" || intake.status === "en_route" || intake.status === "arrived"
    ? intake.sla_deadline : null;
  const msLeft = useCountdown(deadline);
  const urgent = msLeft != null && msLeft < 10 * 60 * 1000;
  const breached = msLeft != null && msLeft < 0;

  const submitAssign = async () => {
    if (!driverId) return;
    setBusy(true);
    const d = drivers.find((x) => x.id === driverId);
    await onAssign(intake.id, { driver_id: driverId, tp_id: d?.tp_id || null, force_uncleared: forceUncleared });
    setBusy(false);
    setAssigning(false);
  };

  const callTarget = (kind, id, phone, name) => {
    callSession?.start?.({ targets: [{ kind, id, phone, name }], purpose: "cpd_coordination" });
  };

  const resolve = async (kind) => {
    setResolving(true);
    try {
      await onResolve(intake.id, kind, resolveReason.trim() || null);
      setResolveOpen(false);
      setResolveReason("");
    } catch { /* surfaced via lane refresh */ } finally {
      setResolving(false);
    }
  };

  const suggestedOption = riskAlert && (riskAlert.options || []).find((o) => o.type === "reassign_driver");
  const actOnRisk = async (action) => {
    if (!riskAlert) return;
    setRiskBusy(true);
    try {
      if (action === "reassign" && suggestedOption) {
        await onResolveRisk(riskAlert.alert_id, "reassign", suggestedOption.driver_id, suggestedOption.tp_id);
      } else if (action === "archive" && intake.trip_id) {
        await onArchiveTrip(intake.trip_id);
      } else {
        await onResolveRisk(riskAlert.alert_id, "dismiss");
      }
    } catch { /* surfaced via lane refresh */ } finally {
      setRiskBusy(false);
    }
  };

  const saveOfficerEmail = async () => {
    const val = emailInput.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      return;
    }
    setEmailSaving(true);
    try {
      await apiClient.patch(`/cpd/intakes/${intake.id}/officer-email`, { officer_email: val });
      intake.officer_email = val;
      setEmailEditing(false);
    } catch { /* surfaced via lane refresh */ } finally {
      setEmailSaving(false);
    }
  };

  const saveOfficerPhone = async () => {
    const digits = phoneInput.replace(/\D/g, "");
    if (digits.length < 10) return;
    setPhoneSaving(true);
    try {
      await apiClient.patch(`/cpd/intakes/${intake.id}/officer-phone`, { officer_phone: phoneInput.trim() });
      intake.officer_phone = phoneInput.trim();
      setPhoneEditing(false);
    } catch { /* surfaced via lane refresh */ } finally {
      setPhoneSaving(false);
    }
  };

  const saveKaizenTripId = async () => {
    setKaizenSaving(true);
    try {
      const { data } = await apiClient.patch(`/cpd/intakes/${intake.id}/kaizen-trip-id`, { kaizen_trip_id: kaizenInput.trim() });
      intake.kaizen_trip_id = data.kaizen_trip_id;
      setKaizenEditing(false);
    } catch { /* surfaced via lane refresh */ } finally {
      setKaizenSaving(false);
    }
  };

  const copyTripId = async () => {
    if (!intake.display_trip_id) return;
    try {
      await navigator.clipboard.writeText(intake.display_trip_id);
      setTripIdCopied(true);
      setTimeout(() => setTripIdCopied(false), 1500);
    } catch { /* clipboard unavailable — no-op */ }
  };

  const generateTripId = async () => {
    const tripId = intake.trip_id || intake.pending_trip_id;
    if (!tripId) return;
    setGenTripIdBusy(true);
    try {
      const { data } = await apiClient.post(`/tpt/trips/${tripId}/generate-display-id`);
      intake.display_trip_id = data.display_trip_id;
    } catch { /* surfaced via lane refresh */ } finally {
      setGenTripIdBusy(false);
    }
  };

  const cm = intake.capture_meta;
  const needsPhoneCheck = cm && !cm.phone_confirmed;
  const emailWasSkipped = cm && cm.email_skipped;
  const isPreTrip = intake.status === "awaiting_va" || intake.status === "claimed";
  const intakeZone = useMemo(() => zoneForAddress(intake.pickup_address), [zoneForAddress, intake.pickup_address]);
  const rankedDrivers = useMemo(() => {
    const withZone = drivers.map((d) => ({ ...d, _same_zone: !!(intakeZone && d.zone_id && d.zone_id === intakeZone.zone_id) }));
    return withZone
      .filter((d) => !sameZoneOnly || d._same_zone)
      .sort((a, b) => (a._same_zone === b._same_zone ? 0 : a._same_zone ? -1 : 1));
  }, [drivers, intakeZone, sameZoneOnly]);

  const archiveThisIntake = async () => {
    setPreTripBusy(true);
    try { await onArchiveIntake(intake.id); } catch { /* surfaced via lane refresh */ } finally { setPreTripBusy(false); }
  };
  const deleteThisIntake = async () => {
    setPreTripBusy(true);
    try { await onDeleteIntake(intake.id); } catch { /* surfaced via lane refresh */ } finally { setPreTripBusy(false); setDeleteConfirming(false); }
  };

  return (
    <div
      className={`cpd-card ${intake.status === "awaiting_va" ? "cpd-card--pending" : ""} ${breached ? "cpd-card--breach" : urgent ? "cpd-card--urgent" : ""}`}
      data-testid={`cpd-intake-${intake.id}`}
    >
      <div className="cpd-card-top">
        <span className="cpd-status-pill" data-testid={`cpd-status-${intake.id}`}>{intake.status.replace(/_/g, " ")}</span>
        <ZoneBadge zoneName={intakeZone?.zone_name} />
        {deadline && (
          <span className="cpd-sla" data-testid={`cpd-sla-${intake.id}`}>{fmtCountdown(msLeft)}</span>
        )}
        {intake.trip_id && !["completed", "cancelled"].includes(intake.status) && (
          <button className="cpd-card-archive" onClick={() => onArchiveTrip(intake.trip_id)}
                  data-testid={`cpd-archive-trip-${intake.id}`}
                  title="Archive this trip instantly — clears it (and any alert) from the Floor">
            archive
          </button>
        )}
        {isPreTrip && (
          <div className="cpd-pretrip-actions" data-testid={`cpd-pretrip-actions-${intake.id}`}>
            <button className="cpd-card-archive cpd-card-archive--soft" onClick={archiveThisIntake} disabled={preTripBusy}
                    data-testid={`cpd-archive-intake-${intake.id}`}
                    title="Archive this case — hides it from the Floor, no driver assigned yet">
              archive
            </button>
            {deleteConfirming ? (
              <>
                <button className="cpd-card-archive cpd-card-archive--confirm" onClick={deleteThisIntake} disabled={preTripBusy}
                        data-testid={`cpd-delete-intake-confirm-${intake.id}`}>
                  {preTripBusy ? "…" : "confirm delete?"}
                </button>
                <button className="cpd-card-archive" onClick={() => setDeleteConfirming(false)}
                        data-testid={`cpd-delete-intake-cancel-${intake.id}`}>
                  cancel
                </button>
              </>
            ) : (
              <button className="cpd-card-archive" onClick={() => setDeleteConfirming(true)} disabled={preTripBusy}
                      data-testid={`cpd-delete-intake-${intake.id}`}
                      title="Permanently delete this case — no driver assigned yet">
                delete
              </button>
            )}
          </div>
        )}
      </div>
      <div className="cpd-officer">
        {intake.officer_name || `Unit ${intake.unit_number || "—"}`}
        {intake.case_number ? <span className="cpd-case"> · case {intake.case_number}</span> : null}
      </div>

      <div className="cpd-trip-id-row" data-testid={`cpd-trip-id-row-${intake.id}`}>
        {intake.display_trip_id ? (
          <span className="cpd-notes" data-testid={`cpd-trip-id-value-${intake.id}`}>
            🆔 Trip ID: <strong>{intake.display_trip_id}</strong>{" "}
            <button className="cpd-call-btn" onClick={copyTripId} data-testid={`cpd-trip-id-copy-${intake.id}`}>
              {tripIdCopied ? "Copied!" : "Copy"}
            </button>
          </span>
        ) : (intake.trip_id || intake.pending_trip_id) ? (
          <button className="cpd-call-btn" onClick={generateTripId} disabled={genTripIdBusy} data-testid={`cpd-trip-id-generate-${intake.id}`}>
            {genTripIdBusy ? "…" : "+ Generate Trip ID (for TP)"}
          </button>
        ) : null}
      </div>

      {riskAlert && (
        <div className="cpd-risk-banner" data-testid={`cpd-risk-banner-${intake.id}`}>
          <div className="cpd-risk-text">
            {riskAlert.kind === "cascading_failure"
              ? `⚠ Nova: ${riskAlert.cascading_note || `driver declined, ${riskAlert.remaining_supply} left nearby`}.`
              : `⚠ Nova: at risk — running ~${riskAlert.projected_delay_min}m behind pickup.`}{" "}
            {suggestedOption
              ? `Suggests reassign to ${suggestedOption.name} (${suggestedOption.distance_mi}mi away, ~${suggestedOption.eta_min}m).`
              : "No nearby replacement driver found."}
          </div>
          <div className="cpd-risk-actions">
            {suggestedOption && (
              <button className="cpd-btn cpd-btn--assign" disabled={riskBusy} onClick={() => actOnRisk("reassign")}
                      data-testid={`cpd-risk-reassign-${intake.id}`}>
                {riskBusy ? "…" : `Reassign to ${suggestedOption.name}`}
              </button>
            )}
            <button className="cpd-call-btn" disabled={riskBusy} onClick={() => actOnRisk("dismiss")}
                    data-testid={`cpd-risk-dismiss-${intake.id}`}>
              Dismiss
            </button>
            {intake.trip_id && (
              <button className="cpd-call-btn cpd-call-btn--warn" disabled={riskBusy} onClick={() => actOnRisk("archive")}
                      data-testid={`cpd-risk-archive-${intake.id}`}
                      title="Stale or test trip? Archive it and clear this alert for good.">
                Archive trip
              </button>
            )}
          </div>
        </div>
      )}
      <div className="cpd-route">
        <div><span className="cpd-route-label">P</span> {intake.pickup_address}</div>
        <div><span className="cpd-route-label">D</span> {intake.dropoff_address}</div>
      </div>
      {intake.notes && <div className="cpd-notes">{intake.notes}</div>}

      <div className="cpd-officer-email" data-testid={`cpd-officer-email-row-${intake.id}`}>
        {emailEditing ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="officer@chicagopolice.org"
              data-testid={`cpd-officer-email-input-${intake.id}`}
              style={{ flex: 1, fontSize: 12, padding: "4px 6px" }}
            />
            <button
              className="cpd-call-btn"
              onClick={saveOfficerEmail}
              disabled={emailSaving}
              data-testid={`cpd-officer-email-save-${intake.id}`}
            >
              {emailSaving ? "…" : "Save"}
            </button>
            <button
              className="cpd-call-btn"
              onClick={() => { setEmailEditing(false); setEmailInput(intake.officer_email || ""); }}
              data-testid={`cpd-officer-email-cancel-${intake.id}`}
            >
              Cancel
            </button>
          </div>
        ) : intake.officer_email ? (
          <span className="cpd-notes" data-testid={`cpd-officer-email-value-${intake.id}`}>
            ✉ {intake.officer_email}{" "}
            <button className="cpd-call-btn" onClick={() => setEmailEditing(true)} data-testid={`cpd-officer-email-edit-${intake.id}`}>edit</button>
          </span>
        ) : (
          <button className="cpd-call-btn" onClick={() => setEmailEditing(true)} data-testid={`cpd-officer-email-add-btn-${intake.id}`}>
            + add officer email
          </button>
        )}
      </div>

      <div className="cpd-officer-phone" data-testid={`cpd-officer-phone-row-${intake.id}`}>
        {phoneEditing ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="312-555-1212"
              data-testid={`cpd-officer-phone-input-${intake.id}`}
              style={{ flex: 1, fontSize: 12, padding: "4px 6px" }}
            />
            <button
              className="cpd-call-btn"
              onClick={saveOfficerPhone}
              disabled={phoneSaving}
              data-testid={`cpd-officer-phone-save-${intake.id}`}
            >
              {phoneSaving ? "…" : "Save"}
            </button>
            <button
              className="cpd-call-btn"
              onClick={() => { setPhoneEditing(false); setPhoneInput(intake.officer_phone || ""); }}
              data-testid={`cpd-officer-phone-cancel-${intake.id}`}
            >
              Cancel
            </button>
          </div>
        ) : (
          <span className="cpd-notes" data-testid={`cpd-officer-phone-value-${intake.id}`}>
            ☏ {intake.officer_phone || "no callback number"}{" "}
            <button className="cpd-call-btn" onClick={() => setPhoneEditing(true)} data-testid={`cpd-officer-phone-edit-${intake.id}`}>edit</button>
          </span>
        )}
      </div>

      <div className="cpd-kaizen-id" data-testid={`cpd-kaizen-id-row-${intake.id}`}>
        {kaizenEditing ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              value={kaizenInput}
              onChange={(e) => setKaizenInput(e.target.value)}
              placeholder="Kaizen trip ID (once Kaizen provides it)"
              data-testid={`cpd-kaizen-id-input-${intake.id}`}
              style={{ flex: 1, fontSize: 12, padding: "4px 6px" }}
            />
            <button
              className="cpd-call-btn"
              onClick={saveKaizenTripId}
              disabled={kaizenSaving}
              data-testid={`cpd-kaizen-id-save-${intake.id}`}
            >
              {kaizenSaving ? "…" : "Save"}
            </button>
            <button
              className="cpd-call-btn"
              onClick={() => { setKaizenEditing(false); setKaizenInput(intake.kaizen_trip_id || ""); }}
              data-testid={`cpd-kaizen-id-cancel-${intake.id}`}
            >
              Cancel
            </button>
          </div>
        ) : intake.kaizen_trip_id ? (
          <span className="cpd-notes" data-testid={`cpd-kaizen-id-value-${intake.id}`}>
            📋 Kaizen ID: {intake.kaizen_trip_id}{" "}
            <button className="cpd-call-btn" onClick={() => setKaizenEditing(true)} data-testid={`cpd-kaizen-id-edit-${intake.id}`}>edit</button>
          </span>
        ) : (
          <button className="cpd-call-btn" onClick={() => setKaizenEditing(true)} data-testid={`cpd-kaizen-id-add-btn-${intake.id}`}>
            + add Kaizen trip ID
          </button>
        )}
      </div>

      {cm && (needsPhoneCheck || emailWasSkipped) && (
        <div className="cpd-capture-flags" data-testid={`cpd-capture-flags-${intake.id}`}>
          {needsPhoneCheck && (
            <span className="cpd-capture-badge cpd-capture-badge--warn" data-testid={`cpd-badge-phone-unverified-${intake.id}`}>
              ⚠ phone not voice-confirmed — verify with officer
            </span>
          )}
          {emailWasSkipped && (
            <span className="cpd-capture-badge" data-testid={`cpd-badge-email-skipped-${intake.id}`}>
              email skipped on call — add manually if known
            </span>
          )}
        </div>
      )}

      {intake.status === "awaiting_va" && intake.dispatch_status === "in_progress" && (
        <div className="cpd-dispatch-progress" data-testid={`cpd-dispatch-progress-${intake.id}`}>
          Auto-dispatch: calling driver {(intake.dispatch_attempt_index ?? 0) + 1} of {intake.dispatch_total_candidates || "?"}
          {intake.dispatch_current_candidate ? ` — ${intake.dispatch_current_candidate}` : ""}…
        </div>
      )}
      {intake.status === "awaiting_va" && intake.dispatch_status === "exhausted" && (
        <div className="cpd-dispatch-exhausted" data-testid={`cpd-dispatch-exhausted-${intake.id}`}>
          Auto-dispatch: nobody accepted — needs manual assign
        </div>
      )}
      {intake.status === "awaiting_va" && intake.dispatch_status === "no_candidates" && (
        <div className="cpd-dispatch-exhausted" data-testid={`cpd-dispatch-nocandidates-${intake.id}`}>
          Auto-dispatch: no available drivers found
        </div>
      )}

      {intake.status === "awaiting_va" && (
        <button className="cpd-btn cpd-btn--claim" onClick={() => onClaim(intake.id)} data-testid={`cpd-claim-btn-${intake.id}`}>
          Claim now
        </button>
      )}

      {intake.status === "claimed" && !assigning && (
        <button className="cpd-btn cpd-btn--assign" onClick={() => setAssigning(true)} data-testid={`cpd-open-assign-${intake.id}`}>
          Assign TP / driver
        </button>
      )}

      {assigning && (
        <div className="cpd-assign-box" data-testid={`cpd-assign-box-${intake.id}`}>
          {intakeZone && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
              <SameZoneToggle checked={sameZoneOnly} onChange={setSameZoneOnly} testId={`cpd-same-zone-${intake.id}`} />
            </div>
          )}
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)} data-testid={`cpd-driver-select-${intake.id}`}>
            <option value="">Select cleared driver…</option>
            {rankedDrivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} — {d.tp_name || "Independent"}{d.zone_name ? ` · ${d.zone_name}${d._same_zone ? " (same zone)" : ""}` : ""}
              </option>
            ))}
          </select>
          <button className="cpd-btn cpd-btn--assign" disabled={!driverId || busy} onClick={submitAssign} data-testid={`cpd-confirm-assign-${intake.id}`}>
            {busy ? "Assigning…" : "Confirm"}
          </button>
          <label className="cpd-force-toggle">
            <input type="checkbox" checked={forceUncleared} onChange={(e) => setForceUncleared(e.target.checked)} data-testid={`cpd-force-uncleared-${intake.id}`} />
            override credentialing
          </label>
        </div>
      )}

      {["assigned", "en_route", "arrived", "onboard", "arrived_dropoff"].includes(intake.status) && (
        <div className="cpd-driver-row">
          <span>{intake.driver_name || "Driver"}</span>
          <button className="cpd-call-btn" onClick={() => callTarget("driver", intake.driver_id, intake.driver_phone, intake.driver_name)} data-testid={`cpd-call-driver-${intake.id}`}>
            ☎ Call driver
          </button>
          {intake.tp_id && (
            <button className="cpd-call-btn" onClick={() => callTarget("tp", intake.tp_id, null, "TP dispatch")} data-testid={`cpd-call-tp-${intake.id}`}>
              ☎ Call TP
            </button>
          )}
        </div>
      )}

      {intake.tracking_token && (
        <>
          <button className="cpd-call-btn" style={{ marginTop: 6 }} onClick={() => setTrackOpen((v) => !v)}
                  data-testid={`cpd-track-toggle-${intake.id}`}>
            {trackOpen ? "hide live tracking" : "◎ live tracking · what the officer sees"}
          </button>
          {trackOpen && (
            <div className="ws-track-frame" style={{ height: 360, marginTop: 6 }} data-testid={`cpd-track-frame-${intake.id}`}>
              <iframe src={`/cpd-track/${intake.tracking_token}`} title="CPD live tracking" loading="lazy" />
            </div>
          )}
        </>
      )}

      {!["awaiting_va", "claimed"].includes(intake.status) && (
        <div className="cpd-resolve-block" data-testid={`cpd-resolve-block-${intake.id}`}>
          {resolveOpen ? (
            <div className="cpd-resolve-box" data-testid={`cpd-resolve-box-${intake.id}`}>
              <input
                type="text"
                value={resolveReason}
                onChange={(e) => setResolveReason(e.target.value)}
                placeholder="Why? (optional — e.g. duplicate, stale data, ran off-app)"
                data-testid={`cpd-resolve-reason-${intake.id}`}
                style={{ fontSize: 12, padding: "4px 6px", width: "100%", marginBottom: 6 }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button className="cpd-btn cpd-btn--assign" disabled={resolving} onClick={() => resolve("complete")}
                        data-testid={`cpd-resolve-complete-${intake.id}`}>
                  {resolving ? "…" : "Mark complete"}
                </button>
                <button className="cpd-btn cpd-btn--claim" disabled={resolving} onClick={() => resolve("cancel")}
                        data-testid={`cpd-resolve-cancel-${intake.id}`}>
                  {resolving ? "…" : "Cancel / archive"}
                </button>
                <button className="cpd-call-btn" onClick={() => setResolveOpen(false)} data-testid={`cpd-resolve-close-${intake.id}`}>
                  back
                </button>
              </div>
            </div>
          ) : (
            <button
              className={`cpd-call-btn ${breached ? "cpd-call-btn--warn" : ""}`}
              onClick={() => setResolveOpen(true)}
              data-testid={`cpd-resolve-open-${intake.id}`}
            >
              {breached ? "⚠ resolve this stuck trip" : "resolve / cancel"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CPDLane() {
  const { callSession, tripRiskAlerts = [], resolveTripRiskAlert, archiveTrip } = useRoom();
  const [intakes, setIntakes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [formLinkOpen, setFormLinkOpen] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formName, setFormName] = useState("");
  const [formSending, setFormSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/cpd/intakes");
      setIntakes(Array.isArray(data) ? data : []);
    } catch { /* silent — peripheral */ }
  };
  const loadDrivers = async () => {
    try {
      const { data } = await apiClient.get("/cpd/available-drivers");
      setDrivers(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load(); loadDrivers();
    const t = setInterval(load, 5000);
    const t2 = setInterval(loadDrivers, 30000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, []);

  const claim = async (id) => {
    await apiClient.post(`/cpd/intakes/${id}/claim`);
    load();
  };
  const assign = async (id, body) => {
    await apiClient.post(`/cpd/intakes/${id}/assign`, body);
    load();
  };
  const resolveIntake = async (id, kind, reason) => {
    await apiClient.post(`/cpd/intakes/${id}/${kind}`, { reason });
    load();
  };
  const archiveIntake = async (id) => {
    await apiClient.post(`/cpd/intakes/${id}/archive`);
    load();
  };
  const deleteIntake = async (id) => {
    await apiClient.delete(`/cpd/intakes/${id}`);
    load();
  };

  const sendManualFormLink = async () => {
    const email = formEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setFormSending(true);
    try {
      await apiClient.post("/cpd/form-links/manual", {
        officer_email: email,
        officer_phone: formPhone.trim() || null,
        officer_name: formName.trim() || null,
      });
      setFormLinkOpen(false);
      setFormEmail(""); setFormPhone(""); setFormName("");
    } catch { /* surfaced via disabled state staying */ } finally {
      setFormSending(false);
    }
  };

  const active = useMemo(() => intakes.filter((i) => !["completed", "cancelled"].includes(i.status) && !i.archived), [intakes]);
  const pendingCount = active.filter((i) => i.status === "awaiting_va").length;

  return (
    <aside className={`cpd-lane ${collapsed ? "cpd-lane--collapsed" : ""} ${pendingCount ? "cpd-lane--alert" : ""}`} data-testid="cpd-lane">
      <header className="cpd-head">
        <div className="cpd-title">
          <span className="cpd-badge">CPD · CHICAGO POLICE</span>
          <span className="cpd-count" data-testid="cpd-intake-count">{active.length}</span>
        </div>
        <button
          className="cpd-call-btn"
          onClick={() => setFormLinkOpen((v) => !v)}
          data-testid="cpd-manual-form-link-toggle"
        >
          + Send form link
        </button>
        {active.length > 0 && (
          <button className="cpd-collapse" onClick={() => setCollapsed((c) => !c)} data-testid="cpd-lane-toggle">
            {collapsed ? "expand" : "collapse"}
          </button>
        )}
      </header>

      {formLinkOpen && (
        <div className="cpd-manual-form-link-box" data-testid="cpd-manual-form-link-box" style={{ padding: "8px 10px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
            For a call transferred straight to you (officer chose the form option) — enter what they gave you verbally.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              placeholder="officer@chicagopolice.org *"
              data-testid="cpd-manual-form-link-email"
              style={{ fontSize: 12, padding: "4px 6px" }}
            />
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Officer name (optional)"
              data-testid="cpd-manual-form-link-name"
              style={{ fontSize: 12, padding: "4px 6px" }}
            />
            <input
              type="tel"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="Callback phone (optional)"
              data-testid="cpd-manual-form-link-phone"
              style={{ fontSize: 12, padding: "4px 6px" }}
            />
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="cpd-btn cpd-btn--assign"
                onClick={sendManualFormLink}
                disabled={formSending || !formEmail.trim()}
                data-testid="cpd-manual-form-link-send"
              >
                {formSending ? "Sending…" : "Send link"}
              </button>
              <button className="cpd-call-btn" onClick={() => setFormLinkOpen(false)} data-testid="cpd-manual-form-link-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!collapsed && active.length > 0 && (
        <div className="cpd-list">
          {active.map((i) => (
            <IntakeCard
              key={i.id} intake={i} drivers={drivers} onClaim={claim} onAssign={assign} onResolve={resolveIntake}
              callSession={callSession}
              riskAlert={tripRiskAlerts.find((a) => a.trip_id === i.trip_id)}
              onResolveRisk={resolveTripRiskAlert}
              onArchiveTrip={archiveTrip}
              onArchiveIntake={archiveIntake}
              onDeleteIntake={deleteIntake}
            />
          ))}
        </div>
      )}
    </aside>
  );
}
